import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Sun } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useQuote } from "@/context/QuoteContext";
import { createLead } from "@/lib/api";

const EMPTY = { name: "", phone: "", email: "", city: "", property_type: "", monthly_bill: "", message: "" };

const QuoteDialog = () => {
  const { open, setOpen, source } = useQuote();
  const [form, setForm] = useState(EMPTY);

  const mutation = useMutation({
    mutationFn: createLead,
    onSuccess: () => {
      toast.success("Request received!", {
        description: "Our solar expert will call you within 24 hours.",
      });
      setForm(EMPTY);
      setOpen(false);
    },
    onError: () => {
      toast.error("Something went wrong", {
        description: "Please try again or call us directly.",
      });
    },
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return toast.error("Please enter your name");
    if (form.phone.trim().length < 6) return toast.error("Please enter a valid phone number");
    mutation.mutate({
      name: form.name.trim(),
      phone: form.phone.trim(),
      email: form.email.trim() || undefined,
      city: form.city.trim() || undefined,
      property_type: form.property_type || undefined,
      monthly_bill: form.monthly_bill ? Number(form.monthly_bill) : undefined,
      message: form.message.trim() || undefined,
      source,
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-[520px] rounded-3xl p-0 overflow-hidden border-0" data-testid="quote-dialog">
        <div className="bg-brand-navy px-6 py-6 md:px-8">
          <DialogHeader className="space-y-2 text-left">
            <div className="flex items-center gap-2 text-brand-yellow">
              <Sun className="h-5 w-5" />
              <span className="text-xs font-bold uppercase tracking-[0.18em]">Free Assessment</span>
            </div>
            <DialogTitle className="text-2xl font-bold text-white">Get Your Free Solar Quote</DialogTitle>
            <DialogDescription className="text-slate-300">
              Share a few details and our team will design a system tailored to your energy needs.
            </DialogDescription>
          </DialogHeader>
        </div>
        <form onSubmit={submit} className="px-6 py-6 md:px-8 space-y-4" data-testid="quote-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="q-name">Full Name *</Label>
              <Input id="q-name" data-testid="quote-input-name" value={form.name} onChange={set("name")} placeholder="Your name" className="rounded-xl" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="q-phone">Phone *</Label>
              <Input id="q-phone" data-testid="quote-input-phone" value={form.phone} onChange={set("phone")} placeholder="+91 ..." className="rounded-xl" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="q-email">Email</Label>
              <Input id="q-email" type="email" data-testid="quote-input-email" value={form.email} onChange={set("email")} placeholder="you@email.com" className="rounded-xl" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="q-city">City</Label>
              <Input id="q-city" data-testid="quote-input-city" value={form.city} onChange={set("city")} placeholder="Hyderabad" className="rounded-xl" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Property Type</Label>
              <Select value={form.property_type} onValueChange={set("property_type")}>
                <SelectTrigger data-testid="quote-select-property" className="rounded-xl">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Residential">Residential</SelectItem>
                  <SelectItem value="Commercial">Commercial</SelectItem>
                  <SelectItem value="Industrial">Industrial</SelectItem>
                  <SelectItem value="Agriculture">Agriculture</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="q-bill">Monthly Bill (₹)</Label>
              <Input id="q-bill" type="number" data-testid="quote-input-bill" value={form.monthly_bill} onChange={set("monthly_bill")} placeholder="e.g. 5000" className="rounded-xl" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="q-msg">Message</Label>
            <Textarea id="q-msg" data-testid="quote-input-message" value={form.message} onChange={set("message")} placeholder="Tell us about your requirement (optional)" className="rounded-xl resize-none" rows={3} />
          </div>
          <Button
            type="submit"
            data-testid="quote-submit-btn"
            disabled={mutation.isPending}
            className="w-full h-12 rounded-full bg-brand-yellow text-brand-navy font-semibold text-base hover:bg-[#f0bd1f] hover:-translate-y-0.5 transition-transform shadow-soft"
          >
            {mutation.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : "Request My Free Quote"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default QuoteDialog;
